import React, { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/AdminLayout';
import { Table } from '../../components/Table';
import { Modal } from '../../components/Modal';
import { PaginationControls } from '../../components/PaginationControls';
import { StatsCards } from '../../components/StatsCards';
import { FilterBar } from '../../components/FilterBar';
import { daaraService, Daara } from '../../services/daaraService';
import api from '../../config/api';

const JOURS = [
  { value: 'lundi', label: 'Lundi' },
  { value: 'mardi', label: 'Mardi' },
  { value: 'mercredi', label: 'Mercredi' },
  { value: 'jeudi', label: 'Jeudi' },
  { value: 'vendredi', label: 'Vendredi' },
  { value: 'samedi', label: 'Samedi' },
  { value: 'dimanche', label: 'Dimanche' },
];

export const AdminDaara: React.FC = () => {
  const [allDaaras, setAllDaaras] = useState<Daara[]>([]);
  const [daaras, setDaaras] = useState<Daara[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedDaara, setSelectedDaara] = useState<Daara | null>(null);
  const [formData, setFormData] = useState<any>({
    titre: '',
    description: '',
    jour: 'lundi',
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState({
    total: 0,
    par_jour: {} as { [key: string]: number },
  });
  const [sectionStats, setSectionStats] = useState<{ VCN: number; HDR: number; SANTE: number }>({
    VCN: 0,
    HDR: 0,
    SANTE: 0,
  });
  const [filters, setFilters] = useState({
    search: '',
    jour: '',
    section: '',
  });

  useEffect(() => {
    fetchDaaras(1);
    fetchStats();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [filters, allDaaras, currentPage]);

  const fetchStats = async () => {
    try {
      const allData = await daaraService.getAll({ page_size: 1000 });
      const allDaaras = allData.results || [];
      
      const jours = ['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'];
      const par_jour: { [key: string]: number } = {};
      jours.forEach(jour => {
        par_jour[jour] = allDaaras.filter((d: Daara) => d.jour === jour).length;
      });
      
      setStats({
        total: allData.count || allDaaras.length,
        par_jour,
      });

      const sectionCounts = { VCN: 0, HDR: 0, SANTE: 0 };
      allDaaras.forEach((daara: Daara) => {
        if (daara.section === 'VCN') {
          sectionCounts.VCN += 1;
        } else if (daara.section === 'HDR') {
          sectionCounts.HDR += 1;
        } else if (daara.section === 'SANTE') {
          sectionCounts.SANTE += 1;
        }
      });
      setSectionStats(sectionCounts);
    } catch (error) {
      console.error('Erreur lors du chargement des stats:', error);
    }
  };

  const fetchDaaras = async (page: number = 1) => {
    setLoading(true);
    try {
      const response = await daaraService.getAll({ page_size: 1000 });
      
      // Gérer la structure de réponse paginée
      if (response && typeof response === 'object') {
        const results = response.results || (Array.isArray(response) ? response : []);
        const allDaarasData = Array.isArray(response) ? response : results;
        
        setAllDaaras(allDaarasData);
        setTotalCount(allDaarasData.length);
      } else {
        setAllDaaras([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Erreur:', error);
      setAllDaaras([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...allDaaras];

    // Filtre par recherche
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter((daara) =>
        daara.titre?.toLowerCase().includes(searchLower) ||
        daara.description?.toLowerCase().includes(searchLower)
      );
    }

    // Filtre par jour
    if (filters.jour) {
      filtered = filtered.filter((daara) => daara.jour === filters.jour);
    }

    // Filtre par section
    if (filters.section) {
      filtered = filtered.filter((daara) => (daara as any).section === filters.section);
    }

    // Trier par date de création décroissante (plus récent en premier)
    filtered.sort((a, b) => {
      const dateA = new Date((a as any).created_at || 0).getTime();
      const dateB = new Date((b as any).created_at || 0).getTime();
      return dateB - dateA;
    });

    // Pagination
    const pageSize = 10;
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedDaaras = filtered.slice(startIndex, endIndex);

    setDaaras(paginatedDaaras.map(formatData) as unknown as unknown as Daara[]);
    setTotalCount(filtered.length);
    setTotalPages(Math.ceil(filtered.length / pageSize));
  };

  const handleCreate = () => {
    setFormData({ titre: '', description: '', jour: 'lundi', section: 'VCN' });
    setSelectedDaara(null);
    setIsModalOpen(true);
  };

  const handleEdit = (daara: Daara) => {
    setFormData({
      titre: daara.titre,
      description: daara.description,
      jour: daara.jour,
      section: daara.section || 'VCN',
    });
    setSelectedDaara(daara);
    setIsModalOpen(true);
  };

  const handleView = (daara: Daara) => {
    setSelectedDaara(daara);
    setIsViewModalOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('titre', formData.titre);
      formDataToSend.append('description', formData.description);
      formDataToSend.append('jour', formData.jour);

      if (formData.photo_principale) {
        formDataToSend.append('photo_principale', formData.photo_principale);
      }
      if (formData.fichier_pdf) {
        formDataToSend.append('fichier_pdf', formData.fichier_pdf);
      }
      if (formData.audio) {
        formDataToSend.append('audio', formData.audio);
      }

      if (selectedDaara) {
        await api.patch(`/daara/daaras/${selectedDaara.id}/`, formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        await api.post('/daara/daaras/', formDataToSend, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      setIsModalOpen(false);
      await fetchDaaras(currentPage);
      fetchStats();
    } catch (error: any) {
      console.error('Erreur:', error);
      alert(error.response?.data?.message || 'Erreur lors de la sauvegarde');
    }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce cours ?')) {
      return;
    }
    try {
      await api.delete(`/daara/daaras/${id}/`);
      await fetchDaaras(currentPage);
      fetchStats();
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const columns = [
    { key: 'titre', label: 'Titre' },
    { key: 'jour', label: 'Jour' },
    {
      key: 'section',
      label: 'Section',
      render: (value: string) => {
        const sections: { [key: string]: string } = {
          VCN: 'VCN (Université)',
          HDR: 'HDR',
          SANTE: 'SANTÉ',
        };
        return (
          <span className="bg-green-100 text-green-800 px-2 py-1 rounded-full text-xs font-semibold">
            {sections[value] || value}
          </span>
        );
      }
    },
    { key: 'nombre_vues', label: 'Vues' },
    { key: 'nombre_likes', label: 'Likes' },
    { key: 'created_at', label: 'Date' },
  ];

  const formatData = (daara: Daara) => ({
    ...daara,
    jour: JOURS.find(j => j.value === daara.jour)?.label || daara.jour,
    created_at: new Date(daara.created_at).toLocaleDateString('fr-FR'),
  });

  return (
    <AdminLayout>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Cours Daara</h1>
          <button
            onClick={handleCreate}
            className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
          >
            + Ajouter un cours
          </button>
        </div>

        {/* Cartes de statistiques */}
        <StatsCards
          stats={[
            { label: 'Total Cours', value: stats.total, icon: '📚', color: 'green' },
            { label: 'Lundi', value: stats.par_jour.lundi || 0, icon: '📖', color: 'blue' },
            { label: 'Mardi', value: stats.par_jour.mardi || 0, icon: '📖', color: 'purple' },
            { label: 'Mercredi', value: stats.par_jour.mercredi || 0, icon: '📖', color: 'orange' },
            { label: 'Jeudi', value: stats.par_jour.jeudi || 0, icon: '📖', color: 'indigo' },
            { label: 'Vendredi', value: stats.par_jour.vendredi || 0, icon: '📖', color: 'teal' },
            { label: 'Samedi', value: stats.par_jour.samedi || 0, icon: '📖', color: 'pink' },
            { label: 'Dimanche', value: stats.par_jour.dimanche || 0, icon: '📖', color: 'cyan' },
          ]}
        />
        <StatsCards
          stats={[
            { label: 'Section VCN', value: sectionStats.VCN, icon: '🎓', color: 'green' },
            { label: 'Section HDR', value: sectionStats.HDR, icon: '🏛️', color: 'indigo' },
            { label: 'Section SANTÉ', value: sectionStats.SANTE, icon: '🩺', color: 'teal' },
          ]}
        />

        {/* Filtres */}
        <FilterBar
          searchPlaceholder="🔍 Rechercher par titre, description..."
          searchValue={filters.search}
          onSearchChange={(value) => {
            setFilters({ ...filters, search: value });
            setCurrentPage(1);
          }}
          filters={[
            {
              key: 'jour',
              label: 'Tous les jours',
              value: filters.jour,
              onChange: (value) => {
                setFilters({ ...filters, jour: value });
                setCurrentPage(1);
              },
              options: JOURS.map(jour => ({
                value: jour.value,
                label: jour.label,
              })),
            },
            {
              key: 'section',
              label: 'Toutes les sections',
              value: filters.section,
              onChange: (value) => {
                setFilters({ ...filters, section: value });
                setCurrentPage(1);
              },
              options: [
                { value: 'VCN', label: '🎓 VCN (Université)' },
                { value: 'HDR', label: '🏛️ HDR' },
                { value: 'SANTE', label: '🩺 SANTÉ' },
              ],
            },
          ]}
          onClear={() => {
            setFilters({ search: '', jour: '', section: '' });
            setCurrentPage(1);
          }}
        />

        {loading ? (
          <div className="flex justify-center items-center py-32">
            <div className="animate-spin rounded-full h-20 w-20 border-4 border-green-200 border-t-green-700"></div>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <Table
                columns={columns}
                data={daaras}
                onEdit={handleEdit}
                onView={handleView}
                onDelete={handleDelete}
              />
            </div>

            {totalPages > 1 && (
              <div className="mt-6">
                  <PaginationControls
                    currentPage={currentPage}
                    totalPages={totalPages}
                    loading={loading}
                    onPageChange={(page) => {
                      setCurrentPage(page);
                    }}
                  />
              </div>
            )}
          </>
        )}

        {/* Modal d'édition/création */}
        {isModalOpen && (
          <Modal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            title={selectedDaara ? 'Modifier le cours' : 'Nouveau cours'}
          >
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input
                  type="text"
                  value={formData.titre}
                  onChange={(e) => setFormData({ ...formData, titre: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jour *</label>
                <select
                  value={formData.jour}
                  onChange={(e) => setFormData({ ...formData, jour: e.target.value })}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                  required
                >
                  {JOURS.map(jour => (
                    <option key={jour.value} value={jour.value}>{jour.label}</option>
                  ))}
                </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Section Hanafia *</label>
                  <select
                    value={formData.section || 'VCN'}
                    onChange={(e) => setFormData({ ...formData, section: e.target.value })}
                    className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                    required
                  >
                    <option value="VCN">VCN (Université)</option>
                    <option value="HDR">HDR</option>
                    <option value="SANTE">SANTÉ</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white resize-none"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Photo principale</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => setFormData({ ...formData, photo_principale: e.target.files?.[0] })}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fichier PDF</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFormData({ ...formData, fichier_pdf: e.target.files?.[0] })}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Audio</label>
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setFormData({ ...formData, audio: e.target.files?.[0] })}
                  className="w-full px-4 py-2 border-2 border-green-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all bg-white"
                />
              </div>

              <div className="flex justify-end space-x-4 pt-4">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-6 py-3 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-xl font-semibold transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-6 py-3 bg-green-600 hover:bg-green-700 text-white rounded-xl font-semibold transition-colors"
                >
                  {selectedDaara ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Modal de visualisation */}
        {isViewModalOpen && selectedDaara && (
          <Modal
            isOpen={isViewModalOpen}
            onClose={() => setIsViewModalOpen(false)}
            title={selectedDaara.titre}
          >
            <div className="space-y-4">
              {selectedDaara.photo_principale && (
                <div>
                  <img
                    src={selectedDaara.photo_principale}
                    alt={selectedDaara.titre}
                    className="w-full h-64 object-cover rounded-xl"
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Jour</label>
                <p className="text-gray-900">{JOURS.find(j => j.value === selectedDaara.jour)?.label || selectedDaara.jour}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <p className="text-gray-900 whitespace-pre-wrap break-words">{selectedDaara.description}</p>
              </div>
              <div className="flex items-center space-x-4">
                <span>👁️ {selectedDaara.nombre_vues} vues</span>
                <span>❤️ {selectedDaara.nombre_likes} likes</span>
                <span>💬 {selectedDaara.nombre_commentaires || 0} commentaires</span>
              </div>
              {selectedDaara.fichier_pdf && (
                <div>
                  <a
                    href={selectedDaara.fichier_pdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-green-600 hover:text-green-700 font-semibold"
                  >
                    📄 Voir le PDF
                  </a>
                </div>
              )}
              {selectedDaara.audio && (
                <div>
                  <audio controls className="w-full">
                    <source src={selectedDaara.audio} />
                  </audio>
                </div>
              )}
            </div>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
};

